import React, { useEffect, useState } from "react";
import api from "../../components/axiosComp";
import RevenueAreaChart from "../../components/charts/RevenueAreaChart";
import UserVenueBarChart from "../../components/charts/UserVenueBarChart";
import VenueDemandHeatmap from "../../components/charts/VenueDemandHeatmap";

// ---------- helpers ----------
const formatNumber = (n) =>
  typeof n === "number" ? n.toLocaleString("en-IN") : "—";

const formatCurrency = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : "—";

const formatDelta = (v) => {
  if (v === null || v === undefined) return "—";
  const num = Number(v);
  if (Number.isNaN(num)) return "—";
  const fixed = num.toFixed(1);
  const sign = num > 0 ? "+" : "";
  return `${sign}${fixed}%`;
};

const deltaClass = (v) => {
  if (v === null || v === undefined) return "sa-kpi-delta--neutral";
  if (v >= 0) return "sa-kpi-delta--up";
  return "sa-kpi-delta--down";
};

const monthLabelFromPeriod = (period) => {
  if (!period) return "";
  const parts = String(period).split("-");
  const m = Number(parts[1]) || 0;
  const labels = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return labels[m - 1] || period;
};

const formatDateShort = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10); // YYYY-MM-DD
};
const bookingStatusClass = (status = "") => {
  const s = String(status).toLowerCase();
  if (s === "confirmed") return "sa-status-pill sa-status-pill--confirmed";
  if (s === "pending") return "sa-status-pill sa-status-pill--pending";
  if (s === "cancelled") return "sa-status-pill sa-status-pill--cancelled";
  return "sa-status-pill";
};

const formatRelativeTime = (value) => {
  if (!value) return "";
  const now = new Date();
  const d = new Date(value);
  const diffMs = now - d;
  if (diffMs < 0) return "just now";

  const diffMin = Math.floor(diffMs / (60 * 1000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;

  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr > 1 ? "s" : ""} ago`;

  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? "s" : ""} ago`;

  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek} week${diffWeek > 1 ? "s" : ""} ago`;

  return formatDateShort(value);
};



// ---------- main component ----------

// Decide role label from va_ctx (same pattern as Layout)
function getAdminRoleLabel() {
  try {
    const ctx = JSON.parse(localStorage.getItem("va_ctx") || "{}");
    const r = String(ctx?.role || "").trim().toLowerCase();
    if (r === "country_admin") return "Country Admin";
    // treat everything else as superadmin for this dashboard
    return "Super Admin";
  } catch (_) {
    return "Super Admin";
  }
}

const Dashboard = () => {
  const headerLabel = getAdminRoleLabel();

  const [loading, setLoading] = useState(true);

  const [metrics, setMetrics] = useState({
    totalVenues: 0,
    activeUsers: 0,
    activeUsersWindowDays: 30,
    revenueMtd: 0,
    revenueLast30ChangePct: null,
    bookingsToday: 0,
    bookingsTodayChangePct: null,
  });

  const [userVenueTrend, setUserVenueTrend] = useState({
    totalNewUsers: 0,
    changePct: null,
    series: [], // [{ period: "YYYY-MM", newUsers, newVenues }]
  });

  const [revenueTrend, setRevenueTrend] = useState({
    totalLast30: 0,
    changePct: null,
    points: [], // [{ period: "YYYY-MM-DD", amount }]
  });

  const [latestBookings, setLatestBookings] = useState([]);


  const [recentActivity, setRecentActivity] = useState([]);
  const [venueAcquisition, setVenueAcquisition] = useState({
    items: [],
    heatmapPoints: [],
    summary: null,
    days: 30,
  });

  const maxNewUsers = Math.max(
    ...userVenueTrend.series.map((row) => row.newUsers || 0),
    0
  );

  const maxRevenue = Math.max(
    ...revenueTrend.points.map((p) => p.amount || 0),
    0
  );




  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const today = new Date();
        const to = today.toISOString().slice(0, 10); // YYYY-MM-DD

        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - 29); // last 30 days
        const from = fromDate.toISOString().slice(0, 10);

        const [
          overviewRes,
          uvRes,
          revRes,
          activityRes,
          bookingsRes,
          venueIntelRes,
        ] = await Promise.all([
          api.get("/api/superAdmin/metrics/overview"),
          api.get("/api/superAdmin/metrics/new-users-vs-venues"),
          api.get("/api/superAdmin/revenue/trend", {
            params: {
              from,
              to,
              granularity: "day",
            },
          }),
          api.get("/api/superAdmin/activity/recent", {
            params: { limit: 5 },
          }),
          api.get("/api/superAdmin/bookings", {
            params: {
              page: 1,
              limit: 4,          // only 4 rows like the design
              quick: "thisMonth" // window = this month, sorted by date desc
            },
          }),
          api.get("/api/superAdmin/metrics/venue-acquisition-intelligence", {
            params: {
              days: 30,
              limit: 8,
            },
          }),
        ]);



        if (cancelled) return;

        // ---- Overview metrics ----
        const m = overviewRes.data?.metrics || {};
        const uv = uvRes.data || {};
        const rev = revRes.data || {};
        const act = activityRes.data || {};
        const bookings = bookingsRes.data || {};
        const venueIntel = venueIntelRes.data || {};

        console.log("SA revenue trend >>>", rev);


        // ---- Overview metrics ----
        setMetrics({
          totalVenues: m.totalVenues ?? 0,

          activeUsers: m.activeUsers?.value ?? 0,
          activeUsersWindowDays: m.activeUsers?.windowDays ?? 30,

          revenueMtd: m.revenue?.monthToDate ?? 0,
          revenueLast30ChangePct: m.revenue?.changePct ?? null,

          bookingsToday: m.bookingsToday?.value ?? 0,
          bookingsTodayChangePct: m.bookingsToday?.changeVsYesterdayPct ?? null,
        });

        // ---- New Users vs New Venues ----
        setUserVenueTrend({
          totalNewUsers: uv.totalNewUsers ?? 0,
          changePct: uv.changePct ?? null,
          series: Array.isArray(uv.data) ? uv.data : [],
        });

        // ---- Revenue trend (Overall Revenue Over Time) ----
        const trendArray = Array.isArray(rev.trend) ? rev.trend : [];
        const points = trendArray.map((row) => ({
          period: row.period,
          amount:
            row.totalAmount ??
            row.total ??
            0, // handle both shapes just in case
        }));

        const totalLast30 = points.reduce(
          (sum, p) => sum + (p.amount || 0),
          0
        );

        setRevenueTrend({
          totalLast30,
          changePct: m.revenue?.changePct ?? null, // same % as top card
          points,
        });
        // ---- Recent Activity ----
        const activityItems = Array.isArray(act.items) ? act.items : [];
        setRecentActivity(activityItems);
        // ---- Latest bookings (table) ----
        const bookingItems = Array.isArray(bookings.items)
          ? bookings.items
          : [];
        setLatestBookings(bookingItems);
        setVenueAcquisition({
          items: Array.isArray(venueIntel.items) ? venueIntel.items : [],
          heatmapPoints: Array.isArray(venueIntel.heatmapPoints)
            ? venueIntel.heatmapPoints
            : [],
          summary: venueIntel.summary || null,
          days: venueIntel.days || 30,
        });

      } catch (err) {
        console.error("SuperAdmin overview metrics error:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);


  return (
    <>
      {/* ===== TOP HEADER (same style as Venue Admin dashboard) ===== */}
      <div
        className="va-dashboard-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <div>
          <h1 className="sa-h1" style={{ marginBottom: 4 }}>
            Welcome, {headerLabel}!
          </h1>
          <div className="sa-note">
            Here’s your business overview for the selected period.
          </div>
        </div>
      </div>



      <div className="sa-dashboard-layout">
        <div className="sa-dashboard-main">


          {/* TOP FOUR KPI CARDS */}
          <section className="sa-cards sa-cards--kpi">
            {/* Total Venues */}
            <article className="sa-card sa-card--kpi">
              <div className="sa-card-kicker">Total Venues</div>
              <div className="sa-card-metric">
                {formatNumber(metrics.totalVenues)}
              </div>
              <div className="sa-card-sub">
                {/* we don't have % change yet for venues, so simple text */}
                All active venues (not deleted)
              </div>
            </article>

            {/* Active Users */}
            <article className="sa-card sa-card--kpi">
              <div className="sa-card-kicker">Active Users</div>
              <div className="sa-card-metric">
                {formatNumber(metrics.activeUsers)}
              </div>
              <div className="sa-card-sub">
                Active in last {metrics.activeUsersWindowDays} days
              </div>
            </article>

            {/* Revenue (MTD) */}
            <article className="sa-card sa-card--kpi">
              <div className="sa-card-kicker">Revenue (MTD)</div>
              <div className="sa-card-metric">
                {formatCurrency(metrics.revenueMtd)}
              </div>
              <div
                className={`sa-card-sub sa-kpi-delta ${deltaClass(
                  metrics.revenueLast30ChangePct
                )}`}
              >
                {formatDelta(metrics.revenueLast30ChangePct)}{" "}
                <span className="sa-kpi-delta-text">last 30 days</span>
              </div>
            </article>

            {/* Bookings Today */}
            <article className="sa-card sa-card--kpi">
              <div className="sa-card-kicker">Bookings Today</div>
              <div className="sa-card-metric">
                {formatNumber(metrics.bookingsToday)}
              </div>
              <div
                className={`sa-card-sub sa-kpi-delta ${deltaClass(
                  metrics.bookingsTodayChangePct
                )}`}
              >
                {formatDelta(metrics.bookingsTodayChangePct)}{" "}
                <span className="sa-kpi-delta-text">vs yesterday</span>
              </div>
            </article>
          </section>
          {/* ROW 2: New User vs New Venue card */}
          {/* ROW 2: Revenue chart + New User vs New Venue */}
          <section className="sa-cards sa-cards--kpi sa-cards--second-row">
            {/* Overall Revenue Over Time */}
            <article className="sa-card sa-card--kpi sa-card--revenue">
              <div className="sa-card-head">
                <div className="sa-card-kicker">Overall Revenue Over Time</div>

                <div className="sa-card-metric">
                  {formatCurrency(revenueTrend.totalLast30)}
                </div>

                <div className="sa-card-sub">
                  Last 30 days{" "}
                  <span className={`sa-kpi-delta ${deltaClass(revenueTrend.changePct)}`}>
                    {formatDelta(revenueTrend.changePct)}
                  </span>
                </div>
              </div>

              <div className="sa-card-chart">
                {revenueTrend.points.length === 0 ? (
                  <div className="sa-placeholder sa-placeholder--chart">No data yet</div>
                ) : (
                  <RevenueAreaChart data={revenueTrend.points} />
                )}
              </div>
            </article>


            {/* New User vs New Venue */}
            <article className="sa-card sa-card--kpi sa-card--uv">
              <div className="sa-card-kicker">New User vs. New Venue</div>

              {/* Big number like "460" */}
              <div className="sa-card-metric sa-card-metric--large">
                {formatNumber(userVenueTrend.totalNewUsers)}
              </div>

              {/* Subtitle: "Last 6 months +15%" */}
              <div className="sa-card-sub">
                Last {userVenueTrend.series.length || 0} months{" "}
                <span
                  className={`sa-kpi-delta ${deltaClass(
                    userVenueTrend.changePct
                  )}`}
                >
                  {formatDelta(userVenueTrend.changePct)}
                </span>
              </div>

              {/* Simple bar chart for months (new users only) */}
              <div className="sa-card-chart">
                {userVenueTrend.series.length === 0 ? (
                  <div className="sa-placeholder sa-placeholder--chart">No data yet</div>
                ) : (
                  <UserVenueBarChart data={userVenueTrend.series} />
                )}
              </div>

            </article>
          </section>
          {/* VENUE ACQUISITION INTELLIGENCE */}
          <section className="sa-table-card">
            <div className="sa-table-header">
              <div>
                <h2>Venue Acquisition Intelligence</h2>
                <div className="sa-note">
                  Local areas where platform users exist compared with nearby active venues.
                </div>
              </div>
            </div>
            <div className="venue-demand-heatmap-card">
              <VenueDemandHeatmap points={venueAcquisition.heatmapPoints} />
            </div>

            <div className="sa-table-wrapper">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Local Area</th>
                    <th>Players in Area</th>
                    <th>Users 2km</th>
                    <th>Users 5km</th>
                    <th>Users 10km</th>
                    <th>Active Venues</th>
                    <th>Demand Score</th>
                    <th>Sales Insight</th>
                  </tr>
                </thead>

                <tbody>
                  {venueAcquisition.items.length === 0 && (
                    <tr>
                      <td colSpan={8} className="sa-table-empty">
                        No venue acquisition data found yet.
                      </td>
                    </tr>
                  )}

                  {venueAcquisition.items.map((item) => (
                    <tr key={`${item.area}-${item.latitude}-${item.longitude}`}>
                      <td>
                        <strong>{item.area || "—"}</strong>
                        <div className="sa-note">{item.country || ""}</div>
                      </td>

                      <td>{formatNumber(item.playersInArea || 0)}</td>
                      <td>{formatNumber(item.usersWithin2Km || item.activeUsersWithin2Km || 0)}</td>
                      <td>{formatNumber(item.usersWithin5Km || item.activeUsersWithin5Km || 0)}</td>
                      <td>{formatNumber(item.usersWithin10Km || item.activeUsersWithin10Km || 0)}</td>
                      <td>{formatNumber(item.activeVenuesWithin5Km || 0)}</td>

                      <td>
                        <strong>{formatNumber(item.demandScore || 0)}</strong>
                      </td>

                      <td>{item.insight || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
          {/* LATEST BOOKINGS TABLE */}
          <section className="sa-table-card">
            <div className="sa-table-header">
              <h2>Latest Bookings</h2>
            </div>

            <div className="sa-table-wrapper">
              <table className="sa-table">
                <thead>
                  <tr>
                    <th>Venue Name</th>
                    <th>User</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {latestBookings.length === 0 && (
                    <tr>
                      <td colSpan={4} className="sa-table-empty">
                        No bookings found for this period.
                      </td>
                    </tr>
                  )}

                  {latestBookings.map((b) => (
                    <tr key={b.bookingId || b._id}>
                      <td>{b.venueName || "—"}</td>
                      <td>{b.userName || "—"}</td>
                      <td>{formatDateShort(b.date)}</td>
                      <td>
                        <span className={bookingStatusClass(b.reservationStatus)}>
                          {b.reservationStatus || "—"}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>



        {/* RIGHT SIDE – Recent Activity */}
        <aside className="sa-dashboard-aside">
          <section className="sa-card sa-card--activity">
            <div className="sa-activity-header">Recent Activity</div>

            <ul className="sa-activity-list">
              {recentActivity.length === 0 && !loading && (
                <li className="sa-activity-empty">No recent activity yet.</li>
              )}

              {recentActivity.map((item) => (
                <li key={item._id} className="sa-activity-item">
                  <div className="sa-activity-icon" />

                  <div className="sa-activity-content">
                    <div className="sa-activity-message">
                      {item.message || "Activity"}
                    </div>
                    <div className="sa-activity-meta">
                      {formatRelativeTime(item.createdAt)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </aside>

      </div>

      {loading && (
        <p className="sa-loading-hint">Loading latest metrics…</p>
      )}

    </>
  );
};

export default Dashboard;
